import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Slot group definitions ───────────────────────────────────────────────────

const place1Def: SlotGroupDefinition = {
  label: 'Featured Place 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'neighborhood_pois[0].poi_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'neighborhood_pois[0].poi_name',
      defaultValue: 'Local favorite',
    },
    title: {
      label: 'Place Name',
      type: 'text',
      defaultField: 'neighborhood_pois[0].poi_name',
      defaultValue: 'Local Favorite #1',
    },
    url: {
      label: 'URL (optional)',
      type: 'url',
      defaultField: 'neighborhood_pois[0].poi_business_url',
      defaultValue: '',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 0,
    fieldMappings: {
      image: 'poi_featured_image',
      alt: 'poi_name',
      title: 'poi_name',
      url: 'poi_business_url',
    },
  },
  generator: {
    prompt:
      'Select one of the most appealing points of interest near this property to show off the surrounding area, preferring one with a real photo. Web search the property location to judge which nearby spots are genuinely worth featuring.',
    context:
      'First of three featured-place cards in the Favorite Places section. Each card must be a different point of interest, chosen from all available neighborhood POIs.',
    sourceCollection: 'neighborhood_pois',
  },
};

const place2Def: SlotGroupDefinition = {
  label: 'Featured Place 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'neighborhood_pois[1].poi_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'neighborhood_pois[1].poi_name',
      defaultValue: 'Local favorite',
    },
    title: {
      label: 'Place Name',
      type: 'text',
      defaultField: 'neighborhood_pois[1].poi_name',
      defaultValue: 'Local Favorite #2',
    },
    url: {
      label: 'URL (optional)',
      type: 'url',
      defaultField: 'neighborhood_pois[1].poi_business_url',
      defaultValue: '',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 1,
    fieldMappings: {
      image: 'poi_featured_image',
      alt: 'poi_name',
      title: 'poi_name',
      url: 'poi_business_url',
    },
  },
  generator: {
    prompt:
      'Select one of the most appealing points of interest near this property to show off the surrounding area, preferring one with a real photo. Web search the property location to judge which nearby spots are genuinely worth featuring.',
    context:
      'Second of three featured-place cards in the Favorite Places section. Each card must be a different point of interest, chosen from all available neighborhood POIs.',
    sourceCollection: 'neighborhood_pois',
  },
};

const place3Def: SlotGroupDefinition = {
  label: 'Featured Place 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'neighborhood_pois[2].poi_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'neighborhood_pois[2].poi_name',
      defaultValue: 'Local favorite',
    },
    title: {
      label: 'Place Name',
      type: 'text',
      defaultField: 'neighborhood_pois[2].poi_name',
      defaultValue: 'Local Favorite #3',
    },
    url: {
      label: 'URL (optional)',
      type: 'url',
      defaultField: 'neighborhood_pois[2].poi_business_url',
      defaultValue: '',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 2,
    fieldMappings: {
      image: 'poi_featured_image',
      alt: 'poi_name',
      title: 'poi_name',
      url: 'poi_business_url',
    },
  },
  generator: {
    prompt:
      'Select one of the most appealing points of interest near this property to show off the surrounding area, preferring one with a real photo. Web search the property location to judge which nearby spots are genuinely worth featuring.',
    context:
      'Third of three featured-place cards in the Favorite Places section. Each card must be a different point of interest, chosen from all available neighborhood POIs.',
    sourceCollection: 'neighborhood_pois',
  },
};

// ─── Place card (shared shape) ────────────────────────────────────────────────

interface PlaceCardSlot {
  image?: string;
  alt?: string;
  title?: string;
  url?: string;
  _target?: string;
}

function PlaceCard({ slot }: { slot: PlaceCardSlot }) {
  const card = (
    <div
      className="relative rounded-xl overflow-hidden group w-full"
      style={{ aspectRatio: '4/5' }}
    >
      {slot.image ? (
        <VibesitesImage
          url={slot.image}
          alt={slot.alt ?? slot.title ?? ''}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
      )}

      {/* Bottom gradient for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />

      {slot.title && (
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="font-body text-base md:text-lg font-semibold text-white leading-snug">
            {slot.title}
          </p>
        </div>
      )}
    </div>
  );

  if (slot.url) {
    return (
      <a
        href={slot.url}
        target={slot._target || '_blank'}
        rel={(slot._target || '_blank') === '_blank' ? 'noopener noreferrer' : undefined}
        className="block"
      >
        {card}
      </a>
    );
  }

  return card;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function FavoritePlaces() {
  const eyebrow = useContentToken('favoriteplaces.eyebrow');

  const place1 = useSlotGroup('favoriteplaces.place_1', place1Def);
  const place2 = useSlotGroup('favoriteplaces.place_2', place2Def);
  const place3 = useSlotGroup('favoriteplaces.place_3', place3Def);

  return (
    <section className="bg-primary py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header row — eyebrow + heading left, body right */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 mb-12 md:mb-16 items-end">
          <div>
            <EditableToken
              path="favoriteplaces.eyebrow"
              defaultValue="The Best of the Best"
              as="p"
              className="font-body text-sm text-[var(--vibe-color-text-on-primary)] mb-3 tracking-wide"
            >
              {eyebrow}
            </EditableToken>

            <RichTextContent
              path="favoriteplaces.heading"
              label="Heading"
              defaultValue="<p>Find Your New Favorite Places</p>"
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-primary-foreground leading-[1.1]"
            />
          </div>

          <RichTextContent
            path="favoriteplaces.body"
            label="Body"
            defaultValue='<p>We&rsquo;re passionate about this city and confident you&rsquo;ll love it too! Explore our curated list of local favorites to discover the city&rsquo;s unique culture and find your new go-to spots.</p>'
            as="div"
            className="favorite-places-body font-body text-sm md:text-base text-[var(--vibe-color-text-on-primary)] leading-relaxed"
          />
        </div>

        {/* 3-column card grid — 1-up on mobile, 3-across from tablet up (never 2x2) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          <EditableSlotGroup
            groupPath="favoriteplaces.place_1"
            definition={place1Def}
            className="block"
          >
            <PlaceCard slot={place1} />
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="favoriteplaces.place_2"
            definition={place2Def}
            className="block"
          >
            <PlaceCard slot={place2} />
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="favoriteplaces.place_3"
            definition={place3Def}
            className="block"
          >
            <PlaceCard slot={place3} />
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .favorite-places-body p { margin-bottom: 0.75rem; }
        .favorite-places-body p:last-child { margin-bottom: 0; }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'FavoritePlaces',
  displayName: 'Favorite Places',
  tokens: {
    'favoriteplaces.eyebrow': {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'The Best of the Best',
    },
    'favoriteplaces.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Find Your New Favorite Places</p>',
    },
    'favoriteplaces.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>We&rsquo;re passionate about this city and confident you&rsquo;ll love it too! Explore our curated list of local favorites to discover the city&rsquo;s unique culture and find your new go-to spots.</p>',
      generator: {
        prompt:
          'Write an inviting intro for the Favorite Places section about exploring the area around this property and discovering local favorites. Match the actual setting — city, suburb, town, or rural area — rather than assuming a city, and web search the property location to ground it in the real place.',
        context:
          'Sits beside the section heading, above three featured neighborhood point-of-interest cards.',
        length: '1–2 sentences',
      },
    },
  },
  slotGroups: {
    'favoriteplaces.place_1': place1Def,
    'favoriteplaces.place_2': place2Def,
    'favoriteplaces.place_3': place3Def,
  },
};

(FavoritePlaces as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FavoritePlaces;
